import type { ReactNode } from 'react';
import { useT } from '../../i18n/i18n';
import { useShell } from '../shell';
import { IconButton } from './Button';
import { Icon } from './Icon';
import { ProgressBar } from './ProgressBar';

// Screen-level top bar.
//   phone / tablet mini (01 / 02 / 03): ← (deep) or nothing (root) · left-aligned title + subtitle ·
//     status pill · avatar (→ Settings, where account / language / sign-out live).
//   tablet: the global bar is rendered by AppShell (ShellTopBar below), so this renders as a
//     pane header (04 right pane / 05 left pane): title + subtitle + actions.
export function TopBar(props: {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  onBack?: () => void;
  right?: ReactNode;
  // Override the shell's status chip (pass null to hide it).
  status?: ReactNode;
  // A screen whose top-right is its own action (Figma 08 "完成") hides the account avatar.
  avatar?: boolean;
  testId?: string;
}) {
  const shell = useShell();
  const { t } = useT();
  const status = props.status === undefined ? shell.statusChip : props.status;
  const avatar = props.avatar !== false;

  if (shell.tier === 'tablet') {
    return (
      <header className="pane-header" data-testid={props.testId}>
        <div className="pane-header-main">
          {props.onBack ? <IconButton icon="arrow_back" label={t('shell.back')} onClick={props.onBack} tone="soft" testId="topbar-back" /> : null}
          <div className="pane-header-text">
            {props.eyebrow ? <p className="eyebrow">{props.eyebrow}</p> : null}
            <h1 className="pane-header-title">{props.title}</h1>
            {props.subtitle ? <p className="pane-header-subtitle">{props.subtitle}</p> : null}
          </div>
        </div>
        <div className="pane-header-side">{props.right}</div>
      </header>
    );
  }

  return (
    <header className="topbar" data-testid={props.testId}>
      <div className={`topbar-lead ${props.onBack ? '' : 'topbar-lead-root'}`}>
        {props.onBack ? <IconButton icon="arrow_back" label={t('shell.back')} onClick={props.onBack} testId="topbar-back" /> : null}
      </div>
      <div className="topbar-center">
        {props.eyebrow ? <p className="eyebrow">{props.eyebrow}</p> : null}
        <h1 className="topbar-title">{props.title}</h1>
        {props.subtitle ? <p className="topbar-subtitle">{props.subtitle}</p> : null}
      </div>
      <div className="topbar-side topbar-side-right">
        {props.right}
        {status}
        {avatar ? (
          <button type="button" className="avatar-hit" aria-label={t('shell.account')} onClick={shell.openAccount} data-testid="topbar-avatar">
            {shell.avatar ?? (
              <span className="avatar">
                <Icon name="person" />
              </span>
            )}
          </button>
        ) : null}
      </div>
    </header>
  );
}

// Global tablet bar (04 / 05 / 06): settings · CURRENT PROPERTY + address · PROGRESS bar + % · status · avatar.
// Presentational — AppShell feeds it the open inspection's real address and progress.
export function ShellTopBar(props: {
  onMenu: () => void;
  onAvatar: () => void;
  property: string | null;
  progressValue: number;
  progressTotal: number;
  status?: ReactNode;
  avatar?: ReactNode;
  // The primary navigation (TopTabNav) as a second row.
  nav?: ReactNode;
  testId?: string;
}) {
  const { t } = useT();
  const pct = props.progressTotal > 0 ? Math.round((props.progressValue / props.progressTotal) * 100) : 0;
  return (
    <header className="shell-topbar" data-testid={props.testId ?? 'shell-topbar'}>
      <IconButton icon="settings" label={t('nav.settings')} onClick={props.onMenu} testId="shell-menu" />
      <div className="shell-topbar-block">
        <p className="eyebrow">{t('shell.currentProperty')}</p>
        <p className={`shell-topbar-property ${props.property ? '' : 'shell-topbar-property-empty'}`}>{props.property ?? t('shell.noProperty')}</p>
      </div>
      <div className="shell-topbar-block">
        <p className="eyebrow">{t('shell.progress')}</p>
        <div className="shell-topbar-progress">
          <ProgressBar value={props.progressValue} total={props.progressTotal} slim />
          <span className="shell-topbar-pct">{t('common.percent', { n: pct })}</span>
        </div>
      </div>
      <div className="shell-topbar-side">
        {props.status}
        <button type="button" className="avatar-hit" aria-label={t('shell.account')} onClick={props.onAvatar} data-testid="shell-avatar">
          {props.avatar ?? (
            <span className="avatar">
              <Icon name="person" />
            </span>
          )}
        </button>
      </div>
      {props.nav ? <div className="shell-topbar-nav">{props.nav}</div> : null}
    </header>
  );
}
